import React, { useId, useMemo, useRef, useState } from "react";
import useSchoolOptions, { SchoolOption } from "./useSchoolOptions";

interface SchoolPickerProps {
  onSelect?: (value: { school: string; serviceId: string }) => void;
  label?: string;
  placeholder?: string;
}

const MAX_RESULTS = 8;

const SchoolPicker: React.FC<SchoolPickerProps> = ({
  onSelect,
  label = "School",
  placeholder = "Start typing your school name, suburb or postcode",
}) => {
  const options = useSchoolOptions();

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selected, setSelected] = useState<SchoolOption | null>(null);

  const reactId = useId();
  const listId = `school-picker-list-${reactId}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeout = useRef<ReturnType<typeof setTimeout>>();

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    // Don't re-open a one-item list for the value just picked.
    if (selected && selected.name.toLowerCase() === q) return [];
    return options
      .filter(option =>
        [option.name, option.suburb, option.postcode].some(field =>
          field?.toLowerCase().includes(q),
        ),
      )
      .slice(0, MAX_RESULTS);
  }, [options, query, selected]);

  const open = matches.length > 0 && isOpen;

  const choose = (option: SchoolOption) => {
    setSelected(option);
    setQuery(option.name);
    setIsOpen(false);
    setActiveIndex(-1);
    onSelect?.({ school: option.name, serviceId: option.serviceId });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setSelected(null);
    setIsOpen(true);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      setActiveIndex(prev => (prev + 1) % Math.max(matches.length, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(prev => (prev <= 0 ? matches.length - 1 : prev - 1));
    } else if (e.key === "Enter") {
      if (open && activeIndex >= 0 && matches[activeIndex]) {
        e.preventDefault();
        choose(matches[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const optionId = (index: number) => `${listId}-option-${index}`;

  return (
    <div className="relative w-full font-secondary">
      <label htmlFor={`${listId}-input`} className="block text-sm font-bold text-[#4a1375] pb-1">
        {label}
      </label>

      <input
        id={`${listId}-input`}
        ref={inputRef}
        type="text"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        className="w-full h-[52px] px-4 rounded-full bg-White text-sm text-[#4a1375] placeholder-[#4a1375]/50 border border-[#4a1375]/40 focus:ring-0 focus:outline-none"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => query && setIsOpen(true)}
        onBlur={() => {
          // Delay so a mousedown on an option is registered before the list closes.
          blurTimeout.current = setTimeout(() => setIsOpen(false), 120);
        }}
      />

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-[51] mt-1 w-full max-h-72 overflow-auto bg-White border border-[#4a1375]/40 rounded-lg shadow-Md"
        >
          {matches.map((option, index) => (
            <li
              key={`${option.serviceId}-${index}`}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              className={`px-4 py-2 text-sm text-[#4a1375] cursor-pointer ${
                index === activeIndex ? "bg-[#4a1375]/10" : "bg-White"
              } hover:bg-[#4a1375]/10`}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={e => {
                // Prevent the input blur from firing before the click selects.
                e.preventDefault();
                if (blurTimeout.current) clearTimeout(blurTimeout.current);
                choose(option);
              }}
            >
              <span className="font-bold">{option.name}</span>
              {(option.suburb || option.postcode) && (
                <span className="block text-xs text-[#4a1375]/60">
                  {[option.suburb, option.postcode].filter(Boolean).join(", ")}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SchoolPicker;
